import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  Renderer2,
  signal,
  ViewChild,
  OnInit,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { LayoutService } from '@app/layout/service/layout.service';
import { NotificationTemplateComponent } from '@app/modules/notifications/presentation/components/notification-template/notification-template.component';
import { AssignedCommercial } from '@core/models/assigned-commercial.model';
import { Client } from '@core/models/client.model';
import { Contact } from '@core/models/contact.model';
import { ExternalApp } from '@core/models/external-app.model';
import { SupervisorContact } from '@core/models/supervisor-contact.model';
import { AssignedCommercialService } from '@core/services/assigned-commercial/assigned-commercial.service';
import { ExternalAppsService } from '@core/services/external-apps/external-apps.service';
import { SessionService } from '@core/services/session/session.service';
import { SupervisorService } from '@core/services/supervisor/supervisor.service';
import { WorkToolsService } from '@modules/configurations/application/work-tools/work-tools.service';
import { PREDEFINED_WORK_TOOLS } from '@modules/configurations/domain/data/predefined-work-tools.data';
import { MenuItem } from 'primeng/api';
import { Button } from 'primeng/button';
import { DockModule } from 'primeng/dock';
import { SpeedDialModule } from 'primeng/speeddial';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { filter, Subscription } from 'rxjs';
import { AppBreadcrumb } from '../app.breadcrumb';
import { AppConfigurator } from '../app.configurator';
import { AppFooter } from '../app.footer';
import { AppSearch } from '../app.search';
import { AppSidebar } from '../app.sidebar';
import { AppTopbar } from '../app.topbar';
import { CommercialDrawerComponent } from '../commercial-drawer/commercial-drawer.component';
import { ImpersonationBannerComponent } from '@modules/impersonation/components/impersonation-banner/impersonation-banner.component';
import { StatusBannerStripComponent } from '@shared/components/status-banner-strip/status-banner-strip.component';
import { LoggerService } from '@core/services/logger/logger.service';

import { generateFaviconUrl } from '@app/lib/utils';

const FAVICON_CACHE_KEY = 'd2d_work_tools_favicons';

@Component({
  selector: 'd2d-layout',
  imports: [
    CommonModule,
    AppTopbar,
    AppSidebar,
    RouterModule,
    AppConfigurator,
    AppBreadcrumb,
    AppFooter,
    AppSearch,
    ToastModule,
    DockModule,
    TooltipModule,
    SpeedDialModule,
    Button,
    NotificationTemplateComponent,
    CommercialDrawerComponent,
    ImpersonationBannerComponent,
    StatusBannerStripComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly externalAppService = inject(ExternalAppsService);
  private readonly workToolsService = inject(WorkToolsService);
  private readonly assignedCommercialService = inject(AssignedCommercialService);
  private readonly supervisorService = inject(SupervisorService);
  private readonly sessionService = inject(SessionService);
  private readonly logger = inject(LoggerService).forContext('LayoutComponent');
  @ViewChild('dockEl', { read: ElementRef }) dockEl!: ElementRef<HTMLElement>;

  overlayMenuOpenSubscription: Subscription;

  menuCloseSubscription: Subscription;

  menuOutsideClickListener: any;

  menuScrollListener: any;

  manager!: Contact;

  client!: Client;

  // Assigned commercial signals
  assignedCommercial = signal<AssignedCommercial | null>(null);
  showCommercialDrawer = signal<boolean>(false);

  // My supervisor signals (e.g. a Comercial's Jefe de Equipo)
  mySupervisor = signal<SupervisorContact | null>(null);
  showSupervisorDrawer = signal<boolean>(false);
  supervisorDrawerHeader = computed(() => {
    const label = this.mySupervisor()?.relationship_label;
    return label ? `Tu ${label.charAt(0).toLowerCase()}${label.slice(1)}` : 'Tu superior';
  });

  // Mobile tools drawer
  isMobile = signal(typeof window !== 'undefined' ? window.innerWidth <= 991 : false);
  showToolsDrawer = signal(false);

  items = computed<MenuItem[]>(() => this.loadExternalApps(this.externalAppService.externalApps()));

  showDock = computed(
    () => this.items().length > 0 && (!this.isMobile() || this.items().length <= 4),
  );
  showToolsFab = computed(() => this.isMobile() && this.items().length > 4);

  speedDialItems: MenuItem[] = [];

  @ViewChild(AppSidebar) appSidebar!: AppSidebar;

  @ViewChild(AppTopbar) appTopBar!: AppTopbar;

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
  ) {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) =>
          this.outsideClickListener(event),
        );
      }
      if (
        (this.layoutService.isHorizontal() ||
          this.layoutService.isSlim() ||
          this.layoutService.isCompact()) &&
        !this.menuScrollListener
      ) {
        this.menuScrollListener = this.renderer.listen(
          this.appSidebar.menuContainer.nativeElement,
          'scroll',
          (_event) => {
            if (this.layoutService.isDesktop()) {
              this.hideMenu();
            }
          },
        );
      }
      if (this.layoutService.layoutState().staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.menuCloseSubscription = this.layoutService.menuClose$.subscribe(() => {
      this.hideMenu();
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();

      const container = document.querySelector('.layout-content-wrapper');
      if (container) {
        (container as HTMLElement).scrollTop = 0;
      }
    });

    this.subscribeToExternalApps();
  }

  ngOnInit(): void {
    this.getWorkTools();
    this.loadAssignedCommercial();
    this.loadMySupervisor();
  }

  private loadAssignedCommercial(): void {
    const session = this.sessionService.getActiveSession();
    const currentClientId = session?.current_client_id;

    this.logger.info('Loading assigned commercial:', {
      hasSession: !!session,
      currentClientId,
      currentClientContext: session?.current_client_context,
    });

    if (currentClientId) {
      this.assignedCommercialService.getCachedAssignedCommercial().subscribe({
        next: (commercial) => {
          this.logger.info('Assigned commercial loaded:', commercial);
          this.assignedCommercial.set(commercial);
          this.setCommercialMenu();
        },
        error: (error) => {
          this.logger.error('Error loading assigned commercial:', error);
          this.assignedCommercial.set(null);
        },
      });
    } else {
      this.logger.info('No current_client_id, skipping commercial load');
    }
  }

  private loadMySupervisor(): void {
    this.supervisorService.getCachedSupervisor().subscribe({
      next: (supervisor) => {
        this.mySupervisor.set(supervisor);
        this.setCommercialMenu();
      },
      error: () => {
        // El backend no siempre puede determinar un superior (ej. MVP solo cubre
        // Comerciales); no es un error para el usuario, simplemente no se muestra.
        this.mySupervisor.set(null);
      },
    });
  }

  private setCommercialMenu(): void {
    const items: MenuItem[] = [];

    if (this.assignedCommercial()) {
      items.push({
        label: 'Contactar comercial',
        icon: 'pi pi-user',
        command: () => {
          this.openCommercialDrawer();
        },
      });
    }

    if (this.mySupervisor()) {
      items.push({
        label: `Contactar a ${this.mySupervisor()!.relationship_label.toLowerCase()}`,
        icon: 'pi pi-users',
        command: () => {
          this.openSupervisorDrawer();
        },
      });
    }

    this.speedDialItems = items;
  }

  openCommercialDrawer(): void {
    this.showCommercialDrawer.set(true);
  }

  openSupervisorDrawer(): void {
    this.showSupervisorDrawer.set(true);
  }

  ngAfterViewInit() {
    this.setDockEffects();
  }

  private subscribeToExternalApps() {
    effect(() => {
      if (this.items()) {
        setTimeout(() => {
          this.setDockEffects();
        }, 200);
      }
    });
  }

  private setDockEffects() {
    const root = this.dockEl?.nativeElement;
    if (!root) return;

    const getLinks = () => Array.from(root.querySelectorAll<HTMLElement>('.p-dock-item-link'));

    const onMove = (e: MouseEvent) => {
      const links = getLinks();
      if (!links.length) return;

      for (const link of links) {
        const rect = link.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.hypot(dx, dy);
        const radius = 120;
        const maxScaleBoost = 0.65;
        const maxLiftPx = 8;

        const influence = Math.max(0, 1 - dist / radius);
        const scale = 1 + influence * maxScaleBoost;
        const lift = influence * maxLiftPx;

        link.style.transform = `translateY(${-lift}px) scale(${scale})`;
      }
    };

    const onLeave = () => {
      const links = getLinks();
      for (const link of links) {
        link.style.transform = 'translateY(0) scale(1)';
      }
    };

    root.addEventListener('mousemove', onMove);
    root.addEventListener('mouseleave', onLeave);
  }

  getWorkTools() {
    const cache = this.getFaviconCache();
    let cacheChanged = false;

    this.workToolsService.getWorkTools().subscribe({
      next: (response) => {
        const apps = response.tools.map((t) => {
          let icon = t.icon_url;
          const hasLocalIcon = icon && !icon.includes('google.com/s2/favicons');

          if (!hasLocalIcon && t.url) {
            if (cache[t.url]) {
              icon = cache[t.url];
            } else {
              icon = generateFaviconUrl(t.url);
              cache[t.url] = icon;
              cacheChanged = true;
            }
          }

          return {
            id: t.id,
            name: t.name,
            icon: icon || 'pi pi-external-link',
            url: t.url,
            active: t.is_active,
          };
        });

        if (cacheChanged) {
          localStorage.setItem(FAVICON_CACHE_KEY, JSON.stringify(cache));
        }

        this.externalAppService.updateApps(apps);
      },
    });
  }

  private getFaviconCache(): Record<string, string> {
    const data = localStorage.getItem(FAVICON_CACHE_KEY);
    return data ? JSON.parse(data) : {};
  }

  loadExternalApps(externalApps: ExternalApp[]) {
    const apps: MenuItem[] = [];

    externalApps.forEach((app) => {
      const active = app.active;
      const url = app.url ?? undefined;
      if (active && !!(url && url.trim())) {
        const src = this.iconPath(app);

        apps.push({
          label: app.name,
          url,
          target: '_blank',
          icon: src,
          ...({ _src: src, _appUrl: url } as any),
        });
      }
    });

    return apps;
  }

  outsideClickListener(event: any) {
    if (this.isOutsideClicked(event)) {
      this.hideMenu();
    }
  }

  isOutsideClicked(event: any) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarButtonEl = document.querySelector('.topbar-left > a');

    return !(
      sidebarEl?.isSameNode(event.target) ||
      sidebarEl?.contains(event.target) ||
      topbarButtonEl?.isSameNode(event.target) ||
      topbarButtonEl?.contains(event.target)
    );
  }

  /** Escape cierra el menú, como en cualquier panel que se abre sobre el contenido. */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    const state = this.layoutService.layoutState();
    if (state.staticMenuMobileActive || state.overlayMenuActive) {
      this.hideMenu();
    }
  }

  hideMenu() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
      // Ver requestMenuClose: en móvil hay que apagar también sidebarActive o
      // el menú sigue visible aunque se cierre (tocarlo lo deja encendido).
      sidebarActive: this.layoutService.isMobile() ? false : prev.sidebarActive,
    }));
    if (this.layoutService.isHorizontal()) {
      this.layoutService.onMenuStateChange({ key: '' });
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  iconPath(app: ExternalApp): string | null {
    const icon = app.icon;
    const name = app.name;

    if (name) {
      const predefined = PREDEFINED_WORK_TOOLS.find(
        (tool) => tool.name.toLowerCase() === name.toLowerCase(),
      );
      if (predefined) return `./icons/socials/${predefined.icon}`;
    }

    if (icon) return icon;

    return '';
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile.set(window.innerWidth <= 991);
  }

  toggleToolsDrawer(): void {
    this.showToolsDrawer.update((v) => !v);
  }

  closeToolsDrawer(): void {
    this.showToolsDrawer.set(false);
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' ',
      );
    }
  }

  containerClass = computed(() => {
    const layoutConfig = this.layoutService.layoutConfig();
    const layoutState = this.layoutService.layoutState();

    return {
      'layout-overlay': layoutConfig.menuMode === 'overlay',
      'layout-static': layoutConfig.menuMode === 'static',
      'layout-slim': layoutConfig.menuMode === 'slim',
      'layout-horizontal': layoutConfig.menuMode === 'horizontal',
      'layout-compact': layoutConfig.menuMode === 'compact',
      'layout-reveal': layoutConfig.menuMode === 'reveal',
      'layout-drawer': layoutConfig.menuMode === 'drawer',
      'layout-overlay-active': layoutState.overlayMenuActive || layoutState.staticMenuMobileActive,
      'layout-mobile-active': layoutState.staticMenuMobileActive,
      'layout-static-inactive':
        layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
      'layout-sidebar-active': layoutState.sidebarActive,
      'layout-sidebar-anchored': layoutState.anchored,
    };
  });

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuCloseSubscription) {
      this.menuCloseSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
